import React, { Component } from 'react';
import '../../../../../styles/components/our-partner.scss';
import { getClientsNew } from '../../../../../models/organization';
import i18n from '../../../../../i18n';

class PartnersSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isVisible: false,
      partners: [],
      displayedPartners: [],
      loading: false,
    };
    this.sectionRef = React.createRef();
    this.animationIntervals = [];
    this.animationTimeouts = [];
    this.rotationIndices = {}; // Track index for each rotation group separately
    this.usedPartnerIds = new Set(); // Track currently displayed partner IDs
  }

  componentDidMount() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setState({ isVisible: true });

            // Load partners only once
            if (this.state.partners.length === 0) {
              this.loadPartners();
            }

            this.observer.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );

    if (this.sectionRef.current) {
      this.observer.observe(this.sectionRef.current);
    }
  }

  componentWillUnmount() {
    if (this.observer && this.sectionRef.current) {
      this.observer.unobserve(this.sectionRef.current);
    }
    // Clear all intervals and timeouts
    this.animationIntervals.forEach((interval) => clearInterval(interval));
    this.animationTimeouts.forEach((timeout) => clearTimeout(timeout));

    // Clear the used partners set
    this.usedPartnerIds.clear();
  }

  // Utility method to sync usedPartnerIds with displayedPartners (failsafe)
  syncUsedPartnerIds = () => {
    const { displayedPartners } = this.state;
    this.usedPartnerIds = new Set(displayedPartners.map((p) => p.id));
  };

  async loadPartners() {
    try {
      this.setState({ loading: true });

      const response = await getClientsNew();

      const partners = (response?.data || []).map((item) => ({
        id: item.id,
        name: item.organization_name,
        name_ar: item.organization_name_ar,
        logo: item.organization_image_url,
      }));

      const initialDisplayed = partners.slice(0, 8);

      // Initialize usedPartnerIds with the first 8 partners
      this.usedPartnerIds = new Set(initialDisplayed.map((p) => p.id));

      this.setState({
        partners,
        displayedPartners: initialDisplayed,
        loading: false,
      });

      // Start rotation only if more than 8
      if (partners.length > 8) {
        // Wait 3 seconds before starting rotation to let users see initial partners
        const initialTimeout = setTimeout(() => {
          this.startRotation();
        }, 3000);
        this.animationTimeouts.push(initialTimeout);
      }
    } catch (error) {
      console.error('Failed to load partners', error);
      this.setState({ loading: false });
    }
  }

  startRotation = () => {
    const { partners } = this.state;

    if (partners.length <= 8) return;

    // Positions to rotate with separate tracking and staggered delays
    const rotationConfig = [
      { positions: [0, 1], direction: 'up', interval: 6000, key: 'topLeft', initialDelay: 0 },
      { positions: [2, 3], direction: 'left', interval: 6000, key: 'topRight', initialDelay: 1500 },
      {
        positions: [4, 5],
        direction: 'right',
        interval: 6000,
        key: 'bottomLeft',
        initialDelay: 3000,
      },
      {
        positions: [6, 7],
        direction: 'down',
        interval: 6000,
        key: 'bottomRight',
        initialDelay: 4500,
      },
    ];

    // Initialize separate index for each rotation group
    rotationConfig.forEach((config) => {
      this.rotationIndices[config.key] = 8; // Start from 9th partner
    });

    rotationConfig.forEach((config) => {
      // Apply initial delay before starting the interval
      const initialTimeout = setTimeout(() => {
        this.rotateGroup(config);

        const interval = setInterval(() => {
          this.rotateGroup(config);
        }, config.interval);

        this.animationIntervals.push(interval);
      }, config.initialDelay);

      this.animationTimeouts.push(initialTimeout);
    });
  };

  // Helper method to get next unique partner
  getNextUniquePartner = (currentIndex) => {
    const { partners } = this.state;
    let attempts = 0;
    let nextIndex = currentIndex;

    // Try to find a partner that's not currently displayed
    // Limit attempts to prevent infinite loop
    while (attempts < partners.length) {
      const candidate = partners[nextIndex % partners.length];

      if (!this.usedPartnerIds.has(candidate.id)) {
        return { partner: candidate, index: nextIndex + 1 };
      }

      nextIndex++;
      attempts++;
    }

    // Fallback: if all partners are somehow in use (shouldn't happen),
    // just return the next one in sequence
    return {
      partner: partners[currentIndex % partners.length],
      index: currentIndex + 1,
    };
  };

  rotateGroup = (config) => {
    if (this.state.partners.length <= 8) return;

    const { displayedPartners } = this.state;

    // Sync usedPartnerIds before rotation (failsafe against drift)
    this.syncUsedPartnerIds();

    let currentIndex = this.rotationIndices[config.key];
    const newDisplayed = [...displayedPartners];
    const partnersToReplace = []; // Track partners being replaced

    config.positions.forEach((position, idx) => {
      // Stagger the animation within the group
      const staggerTimeout = setTimeout(() => {
        // Get the partner that's about to be replaced
        const oldPartner = displayedPartners[position];

        // Get next unique partner
        const { partner: nextPartner, index: newIndex } = this.getNextUniquePartner(currentIndex);
        currentIndex = newIndex;

        // Track the replacement
        partnersToReplace.push({ position, oldPartner, nextPartner });

        // Add sliding out class
        const element = document.querySelector(`[data-position="${position}"]`);
        if (element) {
          element.classList.add(`slide-out-${config.direction}`);

          // Replace content after slide-out animation completes
          const replaceTimeout = setTimeout(() => {
            // Remove old partner ID from used set
            this.usedPartnerIds.delete(oldPartner.id);

            // Add new partner ID to used set
            this.usedPartnerIds.add(nextPartner.id);

            // Update displayed partners
            newDisplayed[position] = nextPartner;
            this.setState({ displayedPartners: newDisplayed });

            // Wait a frame for DOM update, then slide in
            requestAnimationFrame(() => {
              const newElement = document.querySelector(`[data-position="${position}"]`);
              if (newElement) {
                newElement.classList.remove(`slide-out-${config.direction}`);
                newElement.classList.add(`slide-in-${config.direction}`);

                // Clean up slide-in class after animation
                const cleanupTimeout = setTimeout(() => {
                  if (newElement) {
                    newElement.classList.remove(`slide-in-${config.direction}`);
                  }
                }, 600);
                this.animationTimeouts.push(cleanupTimeout);
              }
            });
          }, 600);
          this.animationTimeouts.push(replaceTimeout);
        }
      }, idx * 150); // 150ms stagger between cards in the same group

      this.animationTimeouts.push(staggerTimeout);
    });

    // Update the index for this rotation group
    this.rotationIndices[config.key] = currentIndex;
  };

  render() {
    const { isVisible, displayedPartners, loading } = this.state;
    const isArabic = i18n.language.startsWith('ar');

    return (
      <section
        ref={this.sectionRef}
        className={`partners-section ${isVisible ? 'animate-in' : ''}`}
      >
        <div className="partners-container">
          {/* Section Header */}
          <div className="partners-header">
            <div className="partners-subtitle">{i18n.t('trusted_by')}</div>
            <h2 className="partners-title">{i18n.t('our_partners')}</h2>
            <div className="partners-divider"></div>
            <p className="partners-description">{i18n.t('partner_description')}</p>
          </div>

          {/* Loading State - Skeleton */}
          {loading && (
            <div className="partners-grid">
              {[...Array(8)].map((_, index) => (
                <div key={`skeleton-${index}`} className="partner-card ps-skeleton-card">
                  <div className="partner-card-inner ps-skeleton-card-inner">
                    <div className="skeleton-logo"></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Partners Grid */}
          {!loading && displayedPartners.length > 0 && (
            <div className="partners-grid">
              {displayedPartners.map((partner, index) => (
                <div
                  key={`${partner.id}-${index}`}
                  data-position={index}
                  className="partner-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="partner-card-inner">
                    <img src={partner.logo} alt={partner.name} className="partner-logo" />
                    <div className="partner-overlay">
                      <span className="partner-name">
                        {isArabic ? partner.name_ar : partner.name}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && displayedPartners.length === 0 && (
            <div className="partners-empty">
              <p>No partners to display</p>
            </div>
          )}

          {/* Animated Background Elements */}
          <div className="partners-bg-decoration">
            <div className="decoration-circle decoration-circle-1"></div>
            <div className="decoration-circle decoration-circle-2"></div>
            <div className="decoration-circle decoration-circle-3"></div>
          </div>
        </div>
      </section>
    );
  }
}

export default PartnersSection;
